/**
 * ElementaryFMSynthNode - FM synthesis using Elementary Audio WASM engine
 *
 * FM (Frequency Modulation) synthesis creates complex timbres by using
 * one oscillator (modulator) to modulate the frequency of another (carrier).
 *
 * Endpoints:
 * - event (input): note events to play
 * - audioOut (output): audio output for routing
 * - harmonicity (input): ratio between carrier and modulator frequencies
 * - modulationIndex (input): depth of modulation (higher = more harmonics)
 * - attack, decay, sustain, release (input): carrier envelope
 * - modAttack, modDecay, modSustain, modRelease (input): modulator envelope
 * - detune (input): global detune in cents
 * - volume (input): output volume in dB
 */

import { ElementarySynthBase, VoiceSlot } from '@elementary/nodes/ElementarySynthBase';
import { Domain } from '@nodes/Endpoint';
import type { AudioDependencies } from '@audio/nodes/AudioBaseNode';
import type { Payload } from '@graph/Payload';
import type { NodeDef } from '@runtime/IRuntime';
import { envelopeEndpoints, synthDefaultEndpoints } from '@audio/sharedEndpoints';

import { ELEMENTARY_FM_PRESETS } from './presets';

// Elementary imports
import { el, NodeRepr_t } from '@elemaudio/core';
import { audio, events, params } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const endpoints = {
  ...audio({
    audioOut: { isOutput: true },
  }),
  ...events({
    event: { isInput: true, primary: true },
  }),
  ...params({
    preset: { dataType: 'string', options: ELEMENTARY_FM_PRESETS.map(p => p.name) },
    volume: { value: -12, min: -60, max: 0, step: 1 },
    ...synthDefaultEndpoints,
    harmonicity: { value: 3, min: 0.1, max: 20, step: 0.1 },
    modulationIndex: { name: 'Mod Index', value: 10, min: 0, max: 100, step: 0.5 },
    detune: { value: 0, min: -1200, max: 1200, step: 1 },
    // Carrier envelope
    envelope: { dataType: 'envelope', isInput: true },
    ...envelopeEndpoints,
    // Modulator envelope
    modAttack: { name: 'Mod A', value: 0.01, min: 0.001, max: 2, step: 0.001, group: 'modEnvelope' },
    modDecay: { name: 'Mod D', value: 0.2, min: 0, max: 2, step: 0.01, group: 'modEnvelope' },
    modSustain: { name: 'Mod S', value: 0.5, min: 0, max: 1, step: 0.01, group: 'modEnvelope' },
    modRelease: { name: 'Mod R', value: 0.5, min: 0, max: 5, step: 0.01, group: 'modEnvelope' },
  })

};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

export class ElementaryFMSynthNode extends ElementarySynthBase<typeof endpoints> {
  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
  }

  // ─────────────────────────────────────────────────────────────
  // Input Handling
  // ─────────────────────────────────────────────────────────────

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'envelope':
        this.setFullEnvelope(payload.value);
        break;
      case 'volume':
      case 'harmonicity':
      case 'modulationIndex':
      case 'detune':
      case 'attack':
      case 'decay':
      case 'sustain':
      case 'release':
      case 'modAttack':
      case 'modDecay':
      case 'modSustain':
      case 'modRelease':
        if (this.isInitialized) {
          this.render();
        }
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // DSP / Audio Graph
  // ─────────────────────────────────────────────────────────────

  /**
   * Build the modulator envelope using the time-based gate.
   * Uses el.const with keys so parameters can update smoothly without graph rebuild.
   */
  private buildModulatorEnvelope(gate: NodeRepr_t): NodeRepr_t {
    return el.adsr(
      el.const({ key: 'mod_env_attack', value: this.params.modAttack.value }),
      el.const({ key: 'mod_env_decay', value: this.params.modDecay.value }),
      el.const({ key: 'mod_env_sustain', value: this.params.modSustain.value }),
      el.const({ key: 'mod_env_release', value: this.params.modRelease.value }),
      gate
    );
  }

  /**
   * Build FM synthesis voice with sample-accurate timing.
   *
   * FM formula: output = sin(carrierFreq + modulationDepth * sin(modulatorFreq))
   * where modulationDepth = carrierFreq * modulationIndex
   */
  private buildFMVoice(slot: VoiceSlot, voiceId: string): NodeRepr_t {
    const baseFreq = 440 * Math.pow(2, (slot.note - 69) / 12);
    const detune = this.params.detune.value;
    const detunedFreq = baseFreq * Math.pow(2, detune / 1200);

    const harmonicity = this.params.harmonicity.value;
    const modulationIndex = this.params.modulationIndex.value;

    const carrierFreq = detunedFreq;
    const modulatorFreq = detunedFreq * harmonicity;
    const modulationDepth = carrierFreq * modulationIndex;

    // Time-based gate signal for sample-accurate timing
    const gateSignal = this.buildTimeGate(voiceId, slot.startSample, slot.endSample);

    // Modulator envelope
    const modEnv = this.buildModulatorEnvelope(gateSignal);

    // Modulator oscillator (sine wave)
    const modFreqConst = el.const({ key: `${voiceId}_modFreq`, value: modulatorFreq });
    const modulator = el.cycle(modFreqConst);

    // Apply modulation envelope and depth
    const modDepthConst = el.const({ key: `${voiceId}_modDepth`, value: modulationDepth });
    const modulatedSignal = el.mul(modulator, el.mul(modEnv, modDepthConst));

    // Carrier frequency = base + modulation
    const carrierFreqConst = el.const({ key: `${voiceId}_carrierFreq`, value: carrierFreq });
    const modulatedFreq = el.add(carrierFreqConst, modulatedSignal);

    // Carrier oscillator (sine wave) with modulated frequency
    const carrier = el.cycle(modulatedFreq);

    // Carrier envelope (uses base class buildEnvelope)
    const carrierEnv = this.buildEnvelope(gateSignal);

    // Apply velocity and envelope
    const velGain = el.const({ key: `${voiceId}_vel`, value: slot.velocity });
    return el.mul(carrier, el.mul(carrierEnv, velGain));
  }

  render(): void {
    if (!this.core || !this.isInitialized) {
      this.logTrace('render skipped: not initialized');
      return;
    }

    // Always render all voice slots - graph structure stays constant
    const voiceOutputs: NodeRepr_t[] = [];

    for (let i = 0; i < this.voiceSlots.length; i++) {
      const slot = this.voiceSlots[i];
      const voiceId = `voice_${i}`;
      voiceOutputs.push(this.buildFMVoice(slot, voiceId));
    }

    // Sum all voices
    let output: NodeRepr_t = voiceOutputs[0];
    for (let i = 1; i < voiceOutputs.length; i++) {
      output = el.add(output, voiceOutputs[i]);
    }

    this.applyVolumeAndRender(output);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const elementaryFMSynthNodeDef: NodeDef<ElementaryFMSynthNode> = {
  type: 'FMSynth',
  shortName: 'elemfm',
  info: 'Elementary Audio FM synth. Carrier + modulator with separate envelopes for rich timbral control.',
  domain: Domain.Audio,
  endpoints,
  factoryPresets: ELEMENTARY_FM_PRESETS,
  ctor: ElementaryFMSynthNode
};
